import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Briefcase,
  ChevronLeft,
  Loader2,
  Mic,
  Pencil,
  Send,
  ShieldCheck,
  Square,
  Trash2,
  X,
} from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import WelfareOfficerChip from "@/components/mirror/WelfareOfficerChip";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, shortFor } from "@/i18n/locale";
import { localDayFor, MYWORK_PULSE_TZ } from "@/lib/localDay";
import { supabase } from "@/integrations/supabase/client";
import { useMyWorkAccess } from "@/hooks/useMyWorkAccess";
import { useVoiceInputWhisper } from "@/hooks/useVoiceInputWhisper";

// "Meu Trabalho" (mywork) — módulo genérico de trabalho/moradia (white-label
// B2B, decisões ee013ee6/f89c881e/33634c8c; task 91e1ce1f). DOIS ATOS:
//
//  A) PULSO DO DIA — mood/energy/productivity/learning 1-5, UM por dia
//     (entry_type='day', entry_day = dia de Europe/Amsterdam, upsert no
//     UNIQUE user_id+entry_day+entry_type). Texto/voz OPCIONAL.
//  B) SOBRE A CASA — RELATO (entry_type='house', N por dia). As PALAVRAS são
//     o principal e ficam no topo (falar OU escrever, mesmo peso); os chips
//     são rótulos do que foi dito, PRÉ-SUGERIDOS pelo mywork-extract e
//     confirmados pelo morador (sugestão nunca vira envio sozinha —
//     f89c881e: o agregado só admite dado OFERECIDO). Uma tela, sem ordem
//     forçada — quem toca o chip antes e fala depois, pode.
//
// kind='question' recebe resposta do Espelho ALI (mywork-react →
// mirror_response); quando o Espelho não sabe, entra o chip de escalada que
// já está em produção (#486, WelfareOfficerChip) — nunca resposta inventada.
//
// ⛔ org_id/site_id: carimbados por trigger, nunca enviados. Voz: recorder
// canônico useVoiceInputWhisper com surface 'mywork' (custo carimbado —
// item 7). Update/delete com .select() e 0 linhas = FALHA (regra 4.9).

const CATEGORIES = [
  "room",
  "cleaning",
  "maintenance",
  "internet",
  "transport",
  "work_shift",
  "documents",
  "health",
  "community",
  "leisure",
  "other",
] as const;

const KINDS = ["question", "complaint", "praise", "suggestion"] as const;

type Category = (typeof CATEGORIES)[number];
type Kind = (typeof KINDS)[number];

// Sugestão do classificador ≠ escolha da pessoa: o envio com 'ai' ainda vale
// como confirmação (a pessoa VIU a marcação e apertou enviar), mas o estado
// visual distinto é obrigatório até lá.
type ChipSource = "user" | "ai";

interface RecentEntry {
  id: string;
  category: string | null;
  kind: string | null;
  note: string | null;
  created_at: string;
}

const CYAN = "hsl(187 72% 50%)";
const CLASSIFY_TIMEOUT_MS = 6000;

// Seletor 1-5 (pulso) — mesma linguagem de chip do resto do app.
function StepScale({
  label,
  value,
  onSelect,
}: {
  label: string;
  value: number | null;
  onSelect: (v: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] font-display text-foreground/75 shrink-0">
        {label}
      </span>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((v) => {
          const active = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onSelect(v)}
              className="w-9 h-9 rounded-xl font-display text-[13px] font-semibold transition-all active:scale-95"
              style={{
                background: active ? "hsl(187 72% 50% / 0.22)" : "rgba(255,255,255,0.04)",
                border: "1px solid " + (active ? "hsl(187 72% 50% / 0.55)" : "rgba(255,255,255,0.08)"),
                color: active ? "hsl(187 72% 70%)" : "rgba(255,255,255,0.55)",
                boxShadow: active ? "0 0 14px hsl(187 72% 50% / 0.18)" : "none",
              }}
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Chip({
  text,
  active,
  suggested,
  onClick,
}: {
  text: string;
  active: boolean;
  /** Selecionado pelo CLASSIFICADOR — visual distinto (tracejado âmbar) até a
      pessoa tocar (vira escolha) ou enviar (confirma). */
  suggested?: boolean;
  onClick: () => void;
}) {
  const suggestedActive = active && suggested;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="px-3 py-1.5 rounded-full font-display text-[12px] tracking-wide transition-all active:scale-95"
      style={{
        background: suggestedActive
          ? "hsl(38 90% 55% / 0.12)"
          : active
            ? "hsl(187 72% 50% / 0.18)"
            : "rgba(255,255,255,0.04)",
        border: suggestedActive
          ? "1px dashed hsl(38 90% 55% / 0.75)"
          : "1px solid " + (active ? "hsl(187 72% 50% / 0.5)" : "rgba(255,255,255,0.09)"),
        color: suggestedActive
          ? "hsl(38 90% 72%)"
          : active
            ? "hsl(187 72% 72%)"
            : "rgba(255,255,255,0.65)",
      }}
    >
      {text}
    </button>
  );
}

// rowId = o work_checkins.id da pergunta que gerou este estado. É ele que o
// chip de escalada manda como work_checkin_id (decisão B2·a da task 9de48f01):
// a ligação escalada→pergunta sai por igualdade, e work_escalated_after_answer
// vira exato em vez de aproximação por janela temporal.
type AnswerState =
  | { status: "loading" }
  | { status: "answered"; text: string; escalate: boolean; rowId: string }
  | { status: "unknown"; rowId: string }
  | null;

const MyWork = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { allowed, isLoading: accessLoading } = useMyWorkAccess();

  // Recorder canônico (iOS fix, amplitude, 402→PaywallSheet) com o custo do
  // STT carimbado no módulo (item 7). UMA instância para as duas seções.
  const stt = useVoiceInputWhisper(lang, { surface: "work" });
  const [voiceTarget, setVoiceTarget] = useState<"pulse" | "house" | null>(null);

  // ── Seção A — pulso do dia ─────────────────────────────────────────────
  const [mood, setMood] = useState<number | null>(null);
  const [energy, setEnergy] = useState<number | null>(null);
  const [productivity, setProductivity] = useState<number | null>(null);
  const [learning, setLearning] = useState<number | null>(null);
  const [pulseNote, setPulseNote] = useState("");
  const [pulseId, setPulseId] = useState<string | null>(null);
  const [pulseSending, setPulseSending] = useState(false);

  // ── Seção B — relato da casa ───────────────────────────────────────────
  const [houseNote, setHouseNote] = useState("");
  const [category, setCategory] = useState<Category | null>(null);
  const [categoryFrom, setCategoryFrom] = useState<ChipSource | null>(null);
  const [kind, setKind] = useState<Kind | null>(null);
  const [kindFrom, setKindFrom] = useState<ChipSource | null>(null);
  // Refs ESPELHADOS das origens dos chips: o classify (async) precisa ler o
  // valor corrente de forma síncrona para nunca sobrescrever escolha da
  // pessoa. Doutrina do §11: updater é puro ou não existe — leitura síncrona
  // de estado é ref espelhado, nunca variável escrita dentro de updater.
  const categoryFromRef = useRef<ChipSource | null>(null);
  const kindFromRef = useRef<ChipSource | null>(null);
  const applyCategoryFrom = (v: ChipSource | null) => {
    categoryFromRef.current = v;
    setCategoryFrom(v);
  };
  const applyKindFrom = (v: ChipSource | null) => {
    kindFromRef.current = v;
    setKindFrom(v);
  };
  const [classifying, setClassifying] = useState(false);
  const [suggestNone, setSuggestNone] = useState(false);
  const lastClassifiedRef = useRef<string>("");
  const [houseSending, setHouseSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [answer, setAnswer] = useState<AnswerState>(null);

  // ── Lista (só relatos da casa) ─────────────────────────────────────────
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const [recentError, setRecentError] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const loadRecent = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await (supabase as any)
        .from("work_checkins")
        .select("id, category, kind, note, created_at")
        .eq("user_id", user.id)
        .eq("entry_type", "house")
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) {
        // Defeito da #511 fechado (item 8): lista que falha NÃO renderiza o
        // estado vazio ("você ainda não enviou nada" seria mentira) — terceiro
        // estado, com retry.
        console.error("[MyWork] recent load failed:", error);
        setRecentError(true);
        return;
      }
      setRecentError(false);
      setRecent((data as RecentEntry[]) || []);
    } catch (err) {
      console.error("[MyWork] recent load threw:", err);
      setRecentError(true);
    }
  }, []);

  const loadPulseToday = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const today = localDayFor(MYWORK_PULSE_TZ);
      const { data, error } = await (supabase as any)
        .from("work_checkins")
        .select("id, mood, energy, productivity, learning, note")
        .eq("user_id", user.id)
        .eq("entry_type", "day")
        .eq("entry_day", today)
        .maybeSingle();
      if (error) {
        console.warn("[MyWork] pulse load failed:", error);
        return;
      }
      if (data) {
        setPulseId(data.id as string);
        setMood((data.mood as number) ?? null);
        setEnergy((data.energy as number) ?? null);
        setProductivity((data.productivity as number) ?? null);
        setLearning((data.learning as number) ?? null);
        setPulseNote((data.note as string) || "");
      }
    } catch (err) {
      console.warn("[MyWork] pulse load threw:", err);
    }
  }, []);

  useEffect(() => {
    if (allowed) {
      void loadRecent();
      void loadPulseToday();
    }
  }, [allowed, loadRecent, loadPulseToday]);

  // ── Voz — um recorder, duas seções ─────────────────────────────────────
  const startVoice = async (target: "pulse" | "house") => {
    if (stt.isListening || stt.isTranscribing) return;
    setVoiceTarget(target);
    try {
      await stt.startListening();
    } catch (err) {
      console.error("[MyWork] mic start failed:", err);
      setVoiceTarget(null);
    }
  };

  // Sem useCallback de propósito: recriada a cada render, fecha sobre o
  // houseNote/pulseNote CORRENTES — nada de escrever variável de fora dentro
  // de updater (§11, pureUpdater).
  const stopVoice = async () => {
    const target = voiceTarget;
    try {
      const text = (await stt.stopListening()).trim();
      if (!text) return;
      if (target === "pulse") {
        setPulseNote(pulseNote ? pulseNote + " " + text : text);
      } else if (target === "house") {
        const merged = houseNote ? houseNote + " " + text : text;
        setHouseNote(merged);
        void classify(merged);
      }
    } finally {
      setVoiceTarget(null);
    }
  };

  // ── Classificador: PROPÕE, nunca aplica sozinho (f89c881e) ─────────────
  // Só preenche chip que a pessoa ainda não escolheu; falha/timeout NUNCA
  // bloqueia nem mostra erro — cai para os chips manuais com a dica neutra.
  const classify = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length < 8 || trimmed === lastClassifiedRef.current) return;
      lastClassifiedRef.current = trimmed;
      setClassifying(true);
      setSuggestNone(false);
      try {
        const result = await Promise.race([
          supabase.functions.invoke("mywork-extract", {
            body: { text: trimmed, language: shortFor(lang) },
          }),
          new Promise<{ data: null; error: Error }>((resolve) =>
            window.setTimeout(
              () => resolve({ data: null, error: new Error("timeout") }),
              CLASSIFY_TIMEOUT_MS,
            ),
          ),
        ]);
        if (result.error || !result.data) {
          console.warn("[MyWork] classify unavailable:", result.error);
          setSuggestNone(true);
          return;
        }
        const cat = (result.data as Record<string, unknown>).category;
        const knd = (result.data as Record<string, unknown>).kind;
        // Escolha da PESSOA manda: sugestão só preenche campo que ela ainda
        // não tocou (leitura síncrona pelos refs espelhados).
        let suggestedSomething = false;
        if (
          typeof cat === "string" &&
          (CATEGORIES as readonly string[]).includes(cat) &&
          categoryFromRef.current !== "user"
        ) {
          setCategory(cat as Category);
          applyCategoryFrom("ai");
          suggestedSomething = true;
        }
        if (
          typeof knd === "string" &&
          (KINDS as readonly string[]).includes(knd) &&
          kindFromRef.current !== "user"
        ) {
          setKind(knd as Kind);
          applyKindFrom("ai");
          suggestedSomething = true;
        }
        // Classificador voltou vazio/inaproveitável → dica neutra guia para
        // os chips manuais (a "resposta vazia" do item 9). Nunca um erro.
        if (!suggestedSomething) setSuggestNone(true);
      } catch (err) {
        console.warn("[MyWork] classify threw:", err);
        setSuggestNone(true);
      } finally {
        setClassifying(false);
      }
    },
    [lang],
  );

  const pickCategory = (c: Category) => {
    setCategory(c);
    applyCategoryFrom("user");
  };
  const pickKind = (k: Kind) => {
    setKind(k);
    applyKindFrom("user");
  };

  const resetHouseForm = () => {
    setHouseNote("");
    setCategory(null);
    applyCategoryFrom(null);
    setKind(null);
    applyKindFrom(null);
    setSuggestNone(false);
    setEditingId(null);
    lastClassifiedRef.current = "";
  };

  // ── Envio do pulso (upsert, um por dia de Amsterdam) ───────────────────
  // PELO MENOS UMA escala basta (revisão da #513): as colunas são anuláveis
  // de propósito e a view do agregado conta cada métrica só onde ela não é
  // NULL (mood_n_cur WHERE mood IS NOT NULL). Exigir as 4 derruba a taxa de
  // resposta do sinal diário sem ganhar nada no dado.
  const canSubmitPulse =
    (mood !== null || energy !== null || productivity !== null || learning !== null) &&
    !pulseSending;

  const submitPulse = async () => {
    if (!canSubmitPulse) return;
    setPulseSending(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("mywork.error"));
        return;
      }
      // ⛔ org_id/site_id nunca enviados (trigger carimba — inclusive no
      // UPDATE do upsert, o trigger roda nos dois). Árbitro = o índice
      // COMPLETO user_id+entry_day+entry_type (parcial daria 42P10, 4b852662).
      const { data, error } = await (supabase as any)
        .from("work_checkins")
        .upsert(
          {
            user_id: user.id,
            entry_type: "day",
            entry_day: localDayFor(MYWORK_PULSE_TZ),
            mood,
            energy,
            productivity,
            learning,
            note: pulseNote.trim() || null,
            lang,
            source: "app",
          },
          { onConflict: "user_id,entry_day,entry_type" },
        )
        .select();
      if (error || !data || data.length === 0) {
        console.error("[MyWork] pulse upsert failed:", error ?? "0 rows returned");
        toast.error(t("mywork.error"));
        return;
      }
      setPulseId(data[0].id as string);
      toast.success(t("mywork.pulse.saved"));
    } catch (err) {
      console.error("[MyWork] pulse upsert threw:", err);
      toast.error(t("mywork.error"));
    } finally {
      setPulseSending(false);
    }
  };

  // ── Envio/edição do relato da casa ─────────────────────────────────────
  // Palavras OBRIGATÓRIAS (chip sem palavra não é relato); chips obrigatórios
  // mas pré-sugeridos — na prática uma ação. Enviar com sugestão marcada É a
  // confirmação do morador (ele viu a marcação âmbar e apertou).
  const canSubmitHouse =
    houseNote.trim().length > 0 && category !== null && kind !== null && !houseSending;

  const submitHouse = async () => {
    if (!canSubmitHouse) return;
    setHouseSending(true);
    const noteSent = houseNote.trim();
    const kindSent = kind as Kind;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("mywork.error"));
        return;
      }

      if (editingId) {
        // Regra 4.9 no UPDATE: com RLS, negação volta 0 linhas SEM erro —
        // 0 linhas é FALHA, nunca sucesso presumido.
        const { data, error } = await (supabase as any)
          .from("work_checkins")
          .update({ note: noteSent, category, kind, lang })
          .eq("id", editingId)
          .eq("user_id", user.id)
          .select();
        if (error || !data || data.length === 0) {
          console.error("[MyWork] update failed:", error ?? "0 rows returned");
          toast.error(t("mywork.error"));
          return;
        }
        toast.success(t("mywork.house.updated"));
        resetHouseForm();
        void loadRecent();
        return;
      }

      const { data, error } = await (supabase as any)
        .from("work_checkins")
        .insert({
          user_id: user.id,
          entry_type: "house",
          category,
          kind,
          note: noteSent,
          lang,
          source: "app",
        })
        .select();
      if (error || !data || data.length === 0) {
        console.error("[MyWork] insert failed:", error ?? "0 rows returned");
        toast.error(t("mywork.error"));
        return;
      }
      toast.success(t("mywork.sent"));
      const rowId = data[0].id as string;
      resetHouseForm();
      void loadRecent();

      // PERGUNTA RECEBE RESPOSTA (item 6): o Espelho responde ali; se não
      // souber, escalada humana — nunca "Recebido, obrigado" seco.
      if (kindSent === "question") {
        void answerQuestion(rowId, noteSent, user.id);
      }
    } catch (err) {
      console.error("[MyWork] house submit threw:", err);
      toast.error(t("mywork.error"));
    } finally {
      setHouseSending(false);
    }
  };

  const answerQuestion = async (rowId: string, question: string, userId: string) => {
    setAnswer({ status: "loading" });
    try {
      const { data, error } = await supabase.functions.invoke("mywork-react", {
        body: { question, language: shortFor(lang) },
      });
      const answerText =
        !error && data && typeof (data as Record<string, unknown>).answer === "string"
          ? ((data as Record<string, unknown>).answer as string)
          : null;
      if (!answerText) {
        // unknown declarado, 402 de cota, timeout ou erro: tudo cai no mesmo
        // lugar honesto — o humano. Nunca resposta inventada.
        setAnswer({ status: "unknown", rowId });
        return;
      }
      setAnswer({ status: "answered", text: answerText, escalate: false, rowId });
      // Grava a resposta na linha (mirror_response é do item 6). Best-effort
      // VISÍVEL: 0 linhas é falha logada (4.9), mas a resposta já está na
      // tela — não bloqueia o morador por causa do registro.
      const { data: upd, error: updErr } = await (supabase as any)
        .from("work_checkins")
        .update({ mirror_response: answerText })
        .eq("id", rowId)
        .eq("user_id", userId)
        .select();
      if (updErr || !upd || upd.length === 0) {
        console.error("[MyWork] mirror_response save failed:", updErr ?? "0 rows returned");
      }
    } catch (err) {
      console.warn("[MyWork] answer flow failed:", err);
      setAnswer({ status: "unknown", rowId });
    }
  };

  // ── Apagar (delete real, com confirmação e guarda 4.9) ─────────────────
  const deleteEntry = async (id: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("mywork.delete.error"));
        return;
      }
      const { data, error } = await (supabase as any)
        .from("work_checkins")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id)
        .select();
      if (error || !data || data.length === 0) {
        console.error("[MyWork] delete failed:", error ?? "0 rows returned");
        toast.error(t("mywork.delete.error"));
        return;
      }
      toast.success(t("mywork.delete.done"));
      if (editingId === id) resetHouseForm();
      void loadRecent();
    } catch (err) {
      console.error("[MyWork] delete threw:", err);
      toast.error(t("mywork.delete.error"));
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const startEdit = (item: RecentEntry) => {
    setEditingId(item.id);
    setHouseNote(item.note || "");
    setCategory(
      item.category && (CATEGORIES as readonly string[]).includes(item.category)
        ? (item.category as Category)
        : null,
    );
    applyCategoryFrom("user");
    setKind(
      item.kind && (KINDS as readonly string[]).includes(item.kind)
        ? (item.kind as Kind)
        : null,
    );
    applyKindFrom("user");
    setSuggestNone(false);
    lastClassifiedRef.current = (item.note || "").trim();
  };

  // Guarda de rota: allow-list resolvida e negada → volta para /modules.
  if (!accessLoading && !allowed) {
    return <Navigate to="/modules" replace />;
  }
  if (accessLoading) {
    return (
      <MobileLayout>
        <div className="flex-1 flex items-center justify-center min-h-[50vh]">
          <div className="w-7 h-7 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </MobileLayout>
    );
  }

  const labelForCategory = (value: string | null): string =>
    value && (CATEGORIES as readonly string[]).includes(value)
      ? t("mywork.category." + value)
      : value || "—";
  const labelForKind = (value: string | null): string =>
    value && (KINDS as readonly string[]).includes(value)
      ? t("mywork.kind." + value)
      : value || "—";

  const sectionAnim = (i: number) => ({
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3, delay: i * 0.05 },
  });

  const listening = stt.isListening;
  const transcribing = stt.isTranscribing;

  const micButton = (target: "pulse" | "house", big: boolean) => {
    if (!stt.supported) return null;
    const activeHere = voiceTarget === target;
    const busyElsewhere = (listening || transcribing) && !activeHere;
    return (
      <button
        type="button"
        disabled={busyElsewhere}
        onClick={() => (activeHere && listening ? void stopVoice() : void startVoice(target))}
        aria-label={
          activeHere && listening ? t("mywork.house.stop") : t("mywork.house.speak")
        }
        className={
          (big ? "h-[76px] w-[76px] rounded-2xl " : "h-11 w-11 rounded-xl ") +
          "shrink-0 inline-flex flex-col items-center justify-center gap-1 font-display text-[10px] font-semibold tracking-wide transition-all active:scale-95 disabled:opacity-35"
        }
        style={{
          background:
            activeHere && listening
              ? "hsl(0 70% 55% / 0.18)"
              : "hsl(187 72% 50% / 0.15)",
          border:
            activeHere && listening
              ? "1px solid hsl(0 70% 55% / 0.55)"
              : "1px solid hsl(187 72% 50% / 0.4)",
          color: activeHere && listening ? "hsl(0 80% 72%)" : "hsl(187 72% 65%)",
          boxShadow:
            activeHere && listening
              ? "0 0 18px hsl(0 70% 55% / " + (0.15 + stt.amplitude * 0.35) + ")"
              : "none",
        }}
      >
        {activeHere && transcribing ? (
          <Loader2 className={big ? "w-6 h-6 animate-spin" : "w-4 h-4 animate-spin"} />
        ) : activeHere && listening ? (
          <Square className={big ? "w-6 h-6" : "w-4 h-4"} />
        ) : (
          <Mic className={big ? "w-6 h-6" : "w-4 h-4"} />
        )}
        {big && (
          <span>
            {activeHere && transcribing
              ? t("mywork.house.transcribing")
              : activeHere && listening
                ? String(stt.recordingSeconds) + "s"
                : t("mywork.house.speak")}
          </span>
        )}
      </button>
    );
  };

  return (
    <MobileLayout className="pb-28">
      {/* Header */}
      <motion.div {...sectionAnim(0)} className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate("/modules")}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <ChevronLeft className="w-4 h-4 text-foreground/70" />
        </button>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.3)" }}
        >
          <Briefcase className="w-5 h-5" style={{ color: CYAN }} />
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-[17px] font-semibold text-foreground leading-tight">
            {t("module.mywork.name")}
          </h1>
          <p className="text-[11px] text-muted-foreground truncate">
            {t("module.mywork.sub")}
          </p>
        </div>
      </motion.div>

      {/* ── SEÇÃO A — Pulso do dia (um por dia, upsert) ─────────────────── */}
      <motion.section
        {...sectionAnim(1)}
        className="rounded-2xl p-4 mb-4 flex flex-col gap-3.5"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
      >
        <h2 className="font-display text-[14px] font-semibold text-foreground/90">
          {t("mywork.blockDay.title")}
        </h2>
        <StepScale label={t("mywork.mood")} value={mood} onSelect={setMood} />
        <StepScale label={t("mywork.energy")} value={energy} onSelect={setEnergy} />
        <StepScale label={t("mywork.productivity")} value={productivity} onSelect={setProductivity} />
        <StepScale label={t("mywork.learning")} value={learning} onSelect={setLearning} />
        <div className="flex items-start gap-2.5">
          <textarea
            value={pulseNote}
            onChange={(e) => setPulseNote(e.target.value)}
            placeholder={t("mywork.pulse.notePlaceholder")}
            rows={2}
            maxLength={2000}
            className="flex-1 rounded-xl p-3 text-[13px] text-foreground placeholder:text-muted-foreground/60 resize-none outline-none"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
          />
          {micButton("pulse", false)}
        </div>
        <button
          type="button"
          disabled={!canSubmitPulse}
          onClick={submitPulse}
          className="w-full h-11 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.98] disabled:opacity-40 inline-flex items-center justify-center gap-2"
          style={{
            background: canSubmitPulse
              ? "linear-gradient(135deg, hsl(187 72% 50% / 0.4), hsl(187 72% 50% / 0.25))"
              : "rgba(255,255,255,0.04)",
            border: "1px solid hsl(187 72% 50% / 0.5)",
            color: "hsl(187 90% 92%)",
          }}
        >
          <Send className="w-4 h-4" />
          {pulseId ? t("mywork.pulse.update") : t("mywork.submit")}
        </button>
      </motion.section>

      {/* ── SEÇÃO B — Sobre a casa: as PALAVRAS mandam ──────────────────── */}
      <motion.section
        {...sectionAnim(2)}
        className="rounded-2xl p-4 mb-4 flex flex-col gap-3"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-[14px] font-semibold text-foreground/90">
            {t("mywork.blockTopic.title")}
          </h2>
          {editingId && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-display px-2 py-0.5 rounded-full"
              style={{ background: "hsl(38 90% 55% / 0.12)", color: "hsl(38 90% 70%)", border: "1px solid hsl(38 90% 55% / 0.3)" }}
            >
              {t("mywork.house.editing")}
              <button type="button" onClick={resetHouseForm} aria-label={t("common.cancel")}>
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>

        {/* O campo de palavras é o PRINCIPAL — topo, falar OU escrever com o
            mesmo peso. Nunca "opcional", nunca no rodapé. */}
        <p className="text-[13px] font-display text-foreground/85">
          {t("mywork.house.wordsLabel")}
        </p>
        <div className="flex items-stretch gap-2.5">
          <textarea
            value={houseNote}
            onChange={(e) => setHouseNote(e.target.value)}
            onBlur={() => void classify(houseNote)}
            placeholder={t("mywork.house.writePlaceholder")}
            rows={3}
            maxLength={2000}
            className="flex-1 rounded-2xl p-3.5 text-[13px] text-foreground placeholder:text-muted-foreground/60 resize-none outline-none"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid hsl(187 72% 50% / 0.25)" }}
          />
          {micButton("house", true)}
        </div>

        {/* Chips = rótulos do que foi dito. Sugestão chega marcada em âmbar
            tracejado; tocar confirma/ajusta; enviar vendo a marcação também é
            confirmação. */}
        {(classifying || categoryFrom === "ai" || kindFrom === "ai" || suggestNone) && (
          <p className="text-[11px] leading-relaxed" style={{ color: "hsl(38 90% 68%)" }}>
            {classifying ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                {t("mywork.suggested.hint")}
              </span>
            ) : suggestNone && categoryFrom !== "ai" && kindFrom !== "ai" ? (
              t("mywork.suggested.none")
            ) : (
              t("mywork.suggested.hint")
            )}
          </p>
        )}

        {/* Dois grupos ROTULADOS (bug 25/08: sem rótulo e sem separação, os
            15 chips liam como UMA nuvem). Micro-rótulo estrutural, 1-2
            palavras, nunca pergunta — a pergunta pertence ao campo de
            palavras acima. Estilo do SectionHeader de Modules. A separação é
            rótulo + espaço + divisor; o âmbar tracejado segue EXCLUSIVO da
            sugestão do classificador. */}
        <div>
          <p
            className="text-[9px] font-display tracking-[0.22em] uppercase mb-2"
            style={{ color: "hsl(187 72% 50% / 0.6)" }}
          >
            {t("mywork.house.categoryLabel")}
          </p>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <Chip
                key={c}
                text={t("mywork.category." + c)}
                active={category === c}
                suggested={category === c && categoryFrom === "ai"}
                onClick={() => pickCategory(c)}
              />
            ))}
          </div>
        </div>
        <div className="pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <p
            className="text-[9px] font-display tracking-[0.22em] uppercase mb-2"
            style={{ color: "hsl(187 72% 50% / 0.6)" }}
          >
            {t("mywork.house.kindLabel")}
          </p>
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => (
              <Chip
                key={k}
                text={t("mywork.kind." + k)}
                active={kind === k}
                suggested={kind === k && kindFrom === "ai"}
                onClick={() => pickKind(k)}
              />
            ))}
          </div>
          {/* "Pergunta" é de outra classe (única que dispara mywork-react e a
              escalada #486) — a linha de ajuda só existe quando ela está
              selecionada: dá legibilidade à ação que mais importa para a
              métrica com zero poluição fora dela. Sem ícone e sem terceira
              cor de chip, de propósito. */}
          {kind === "question" && (
            <p className="text-[11px] leading-relaxed mt-2" style={{ color: "hsl(187 40% 80%)" }}>
              {t("mywork.house.questionHint")}
            </p>
          )}
        </div>

        {houseNote.trim().length === 0 && (
          <p className="text-[11px] text-muted-foreground/60">{t("mywork.house.needWords")}</p>
        )}

        <button
          type="button"
          disabled={!canSubmitHouse}
          onClick={submitHouse}
          className="w-full h-12 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all active:scale-[0.98] disabled:opacity-40 inline-flex items-center justify-center gap-2"
          style={{
            background: canSubmitHouse
              ? "linear-gradient(135deg, hsl(187 72% 50% / 0.4), hsl(187 72% 50% / 0.25))"
              : "rgba(255,255,255,0.04)",
            border: "1px solid hsl(187 72% 50% / 0.5)",
            color: "hsl(187 90% 92%)",
          }}
        >
          <Send className="w-4 h-4" />
          {editingId ? t("mywork.house.save") : t("mywork.submit")}
        </button>
      </motion.section>

      {/* Resposta do Espelho a uma pergunta (item 6) */}
      {answer && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-4 mb-4"
          style={{ background: "hsl(187 72% 50% / 0.07)", border: "1px solid hsl(187 72% 50% / 0.25)" }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <p className="font-display text-[13px] font-semibold" style={{ color: "hsl(187 72% 70%)" }}>
              {t("mywork.answer.title")}
            </p>
            {answer.status !== "loading" && (
              <button type="button" onClick={() => setAnswer(null)} aria-label={t("common.cancel")}>
                <X className="w-3.5 h-3.5 text-foreground/50" />
              </button>
            )}
          </div>
          {answer.status === "loading" && (
            <p className="text-[12px] text-foreground/70 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t("mywork.answer.loading")}
            </p>
          )}
          {answer.status === "answered" && (
            <div className="flex flex-col gap-2">
              <p className="text-[13px] text-foreground/85 leading-relaxed">{answer.text}</p>
              {/* Escalada TAMBÉM na resposta entregue (revisão da #513): a
                  orientação é GERAL por contrato e pode não servir para o caso
                  da pessoa; e o operador precisa medir "o Espelho respondeu
                  mas ainda precisou de humano". Some quando o chip abre. */}
              {!answer.escalate && (
                <button
                  type="button"
                  onClick={() =>
                    setAnswer({ status: "answered", text: answer.text, escalate: true, rowId: answer.rowId })
                  }
                  className="self-start text-[12px] underline underline-offset-2 text-foreground/60 hover:text-foreground/85 transition-colors"
                >
                  {t("mywork.answer.notResolved")}
                </button>
              )}
              {answer.escalate && (
                <WelfareOfficerChip
                  sessionId={null}
                  origin="my_work"
                  workCheckinId={answer.rowId}
                  onSent={() => {
                    setAnswer(null);
                    toast.success(t("mywork.sent"));
                  }}
                  onDismiss={() =>
                    // Desistiu da escalada: o chip fecha, a resposta FICA.
                    setAnswer({ status: "answered", text: answer.text, escalate: false, rowId: answer.rowId })
                  }
                />
              )}
            </div>
          )}
          {answer.status === "unknown" && (
            <div className="flex flex-col gap-2">
              <p className="text-[12px] text-foreground/75 leading-relaxed">
                {t("mywork.answer.unknown")}
              </p>
              {/* Chip de escalada JÁ EM PRODUÇÃO (#486): consentimento
                  embutido, trigger carimba org/site, fila do operador. */}
              <WelfareOfficerChip
                sessionId={null}
                origin="my_work"
                workCheckinId={answer.rowId}
                onSent={() => {
                  setAnswer(null);
                  toast.success(t("mywork.sent"));
                }}
                onDismiss={() => setAnswer(null)}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* 🔴 Linha de privacidade — obrigatória e visível (ee013ee6). */}
      <motion.div
        {...sectionAnim(3)}
        className="flex items-start gap-2.5 rounded-xl p-3 mb-4"
        style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.2)" }}
      >
        <ShieldCheck className="w-4 h-4 shrink-0 mt-[1px]" style={{ color: "hsl(187 72% 62%)" }} />
        <p className="text-[11.5px] leading-relaxed" style={{ color: "hsl(187 40% 80%)" }}>
          {t("mywork.privacy")}
        </p>
      </motion.div>

      {/* Últimos 10 relatos da casa — com texto, edição e apagar */}
      <motion.section {...sectionAnim(4)} className="mt-2">
        <h2 className="font-display text-[13px] font-semibold text-foreground/80 mb-2.5">
          {t("mywork.recent.title")}
        </h2>
        {recentError ? (
          <div
            className="rounded-xl p-3.5 flex items-center justify-between gap-3"
            style={{ background: "rgba(248,113,113,0.07)", border: "1px solid rgba(248,113,113,0.3)" }}
          >
            <p className="text-[12px]" style={{ color: "rgba(254,226,226,0.9)" }}>
              {t("mywork.recent.error")}
            </p>
            <button
              type="button"
              onClick={() => void loadRecent()}
              className="text-[12px] font-display font-semibold underline underline-offset-2 shrink-0"
              style={{ color: "hsl(187 72% 65%)" }}
            >
              {t("common.tryAgain")}
            </button>
          </div>
        ) : recent.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/60">{t("mywork.empty")}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((r) => (
              <div
                key={r.id}
                className="rounded-xl px-3.5 py-2.5"
                style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] text-foreground/80 truncate">
                    {labelForCategory(r.category)}
                    <span className="text-muted-foreground/60"> · {labelForKind(r.kind)}</span>
                  </span>
                  <span className="text-[11px] text-muted-foreground/60 shrink-0">
                    {new Date(r.created_at).toLocaleString(localeTagFor(lang), {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
                {r.note && (
                  <p className="text-[12px] text-muted-foreground/80 mt-1 leading-snug">
                    {r.note.length > 90 ? r.note.slice(0, 90) + "…" : r.note}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  {deleteConfirmId === r.id ? (
                    <>
                      <span className="text-[11px]" style={{ color: "rgba(254,226,226,0.9)" }}>
                        {t("mywork.delete.confirm")}
                      </span>
                      <button
                        type="button"
                        onClick={() => void deleteEntry(r.id)}
                        className="text-[11px] font-display font-semibold px-2 py-1 rounded-lg"
                        style={{ background: "rgba(248,113,113,0.15)", color: "rgb(252,165,165)", border: "1px solid rgba(248,113,113,0.4)" }}
                      >
                        {t("mywork.delete.yes")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(null)}
                        className="text-[11px] font-display px-2 py-1 rounded-lg text-foreground/60"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)" }}
                      >
                        {t("common.cancel")}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        aria-label={t("mywork.house.edit")}
                        className="inline-flex items-center gap-1 text-[11px] text-foreground/55 px-2 py-1 rounded-lg transition-colors"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(r.id)}
                        aria-label={t("mywork.delete.yes")}
                        className="inline-flex items-center gap-1 text-[11px] text-foreground/55 px-2 py-1 rounded-lg transition-colors"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.section>

    </MobileLayout>
  );
};

export default MyWork;
